import { DatePicker, TimePicker } from '@mui/lab';
import { Tooltip, useEventCallback } from '@mui/material';
import { Grid } from '@mui/material';
import { FormikErrors, FormikTouched } from 'formik';
import { DateTime } from 'luxon';
import { TypeDomainPractice } from 'practice/constants/type-practice-domain';
import { TypeRegister } from 'practice/constants/type-register';
import { AcquisitionSystemItemVM } from 'practice/view-models/acquisition-system-list';
import { memo } from 'react';
import { PmiTextField } from 'shell/components/shared/text-field';
import PracticeTextList, { CodiceDominioPraticaTipo11 } from './practice-text';
import AcquisitionSystemList from 'practice/components/shared/acquisition-system-list';

export interface CancellationPracticeVM {
  closureDate?: DateTime | null;
  closureTime?: DateTime | null;
  closureReason?: string;
  acquisitionSystem?: AcquisitionSystemItemVM | null;
  observationDate?: DateTime | null;
  numberProtocol?: string;
  censusYear?: string;
  censusService?: string;
  censusSheet?: string;
  verificationDate?: DateTime | null;
  censusReason?: AcquisitionSystemItemVM | null;
}

interface AccordionNegativeClosurePracticeFormProps {
  disabled?: boolean;
  values?: CancellationPracticeVM;
  errors?: FormikErrors<CancellationPracticeVM>;
  touched?: FormikTouched<CancellationPracticeVM>;
  onChange: (
    key: keyof CancellationPracticeVM,
    value: CancellationPracticeVM[keyof CancellationPracticeVM]
  ) => void;
  typeRegister?: TypeRegister;
  hideReasonBox?: boolean;
}

const AccordionNegativeClosurePracticeFormComponent = ({
  disabled,
  values,
  onChange,
  touched,
  errors,
  typeRegister,
  hideReasonBox,
}: AccordionNegativeClosurePracticeFormProps): React.ReactElement => {
  const onChangePracticeText = useEventCallback(
    (value: string | undefined, item?: AcquisitionSystemItemVM) => {
      onChange('acquisitionSystem', item);
    }
  );

  const onChangeCensusReason = useEventCallback(
    (value: string | undefined, item?: AcquisitionSystemItemVM) => {
      onChange('censusReason', item);
    }
  );

  const hasTypeClosure =
    typeRegister === TypeRegister.Immigration || typeRegister === TypeRegister.Residence;

  const isCensus = typeRegister === TypeRegister.UntraceableCensus;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={8}>
        <DatePicker
          label="Data chiusura *"
          aria-label="Data chiusura"
          value={values?.closureDate || null}
          onChange={(date) => onChange('closureDate', date)}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              {...params}
              id="closureDate"
              fullWidth
              error={touched?.closureDate && Boolean(errors?.closureDate)}
              helperText={errors?.closureDate}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={4}>
        <TimePicker
          label="Ora chiusura *"
          value={values?.closureTime || null}
          onChange={(date) => {
            onChange('closureTime', date);
          }}
          disabled={disabled}
          renderInput={(params) => (
            <PmiTextField
              id="closureTime"
              {...params}
              fullWidth
              error={touched?.closureTime && Boolean(errors?.closureTime)}
              helperText={errors?.closureTime}
            />
          )}
        />
      </Grid>
      <Grid item xs={12}>
        <PmiTextField
          id="closureReason"
          label="Motivo chiusura*"
          aria-label="motivo"
          fullWidth
          multiline
          rows={5}
          style={{ display: hideReasonBox ? 'none' : 'block' }}
          inputProps={{
            maxLength: '100',
          }}
          onChange={(event) => {
            onChange('closureReason', event.target.value);
          }}
          value={values?.closureReason || ''}
          error={touched?.closureReason && Boolean(errors?.closureReason)}
          helperText={errors?.closureReason}
        />
      </Grid>
      {hasTypeClosure ? (
        <>
          <Grid item xs={12} md={4}>
            <Tooltip
              arrow
              title={
                values?.acquisitionSystem?.descriptionValue && hasTypeClosure
                  ? values?.acquisitionSystem?.descriptionValue
                  : ''
              }
            >
              <PracticeTextList
                onChange={onChangePracticeText}
                value={values?.acquisitionSystem?.id?.toString() || ''}
                typePractice={TypeDomainPractice.Tipo11}
                label="Testo Pratica"
              />
            </Tooltip>
          </Grid>
          {values?.acquisitionSystem?.id === CodiceDominioPraticaTipo11.OsservazioniNegative ? (
            <>
              <Grid item xs={12} md={4}>
                <DatePicker
                  label="Data osservazioni *"
                  value={values?.observationDate || null}
                  onChange={(date) => {
                    onChange('observationDate', date);
                  }}
                  disabled={disabled}
                  renderInput={(params) => (
                    <PmiTextField
                      id="observationDate"
                      {...params}
                      fullWidth
                      error={touched?.observationDate && Boolean(errors?.observationDate)}
                      helperText={errors?.observationDate}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PmiTextField
                  id="numberProtocol"
                  label="Protocollo nr *"
                  aria-label="Protocollo numero"
                  fullWidth
                  inputProps={{
                    maxLength: '100',
                  }}
                  onChange={(event) => {
                    onChange('numberProtocol', event.target.value);
                  }}
                  value={values?.numberProtocol || ''}
                  error={touched?.numberProtocol && Boolean(errors?.numberProtocol)}
                  helperText={errors?.numberProtocol}
                />
              </Grid>
            </>
          ) : null}
        </>
      ) : null}
      {isCensus ? (
        <>
          <Grid item xs={12} md={3}>
            <PmiTextField
              id="censusYear"
              label="Anno censimento *"
              aria-label="Anno censimento"
              fullWidth
              inputProps={{
                maxLength: '4',
              }}
              onChange={(event) => {
                onChange('censusYear', event.target.value);
              }}
              value={values?.censusYear || ''}
              error={touched?.censusYear && Boolean(errors?.censusYear)}
              helperText={errors?.censusYear}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              id="censusService"
              label="Sezione censimento *"
              aria-label="Sezione censimento"
              fullWidth
              onChange={(event) => {
                onChange('censusService', event.target.value);
              }}
              value={values?.censusService || ''}
              error={touched?.censusService && Boolean(errors?.censusService)}
              helperText={errors?.censusService}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              id="censusSheet"
              label="Foglio censimento *"
              aria-label="Foglio censimento"
              fullWidth
              onChange={(event) => {
                onChange('censusSheet', event.target.value);
              }}
              value={values?.censusSheet || ''}
              error={touched?.censusSheet && Boolean(errors?.censusSheet)}
              helperText={errors?.censusSheet}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DatePicker
              label="Data operazione di verifica *"
              value={values?.verificationDate || null}
              onChange={(date) => {
                onChange('verificationDate', date);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <PmiTextField
                  id="verificationDate"
                  {...params}
                  fullWidth
                  error={touched?.verificationDate && Boolean(errors?.verificationDate)}
                  helperText={errors?.verificationDate}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <AcquisitionSystemList
              onChange={onChangeCensusReason}
              value={values?.censusReason?.id?.toString() || ''}
              typePractice={TypeDomainPractice.Tipo13}
              label="Motivo compilazione censimento *"
              error={touched?.censusReason && Boolean(errors?.censusReason)}
            />
          </Grid>
        </>
      ) : null}
    </Grid>
  );
};

AccordionNegativeClosurePracticeFormComponent.displayName =
  'Accordion negative closure practice form component';

const AccordionNegativeClosurePracticeForm = memo(AccordionNegativeClosurePracticeFormComponent);

export default AccordionNegativeClosurePracticeForm;
